export type FormValues = { [name: string]: string };
export type SectionInputs = {
  [sectionKey: string]: FormValues | { [subsectionKey: string]: FormValues };
};
export type KeyValuePair = { key: string; value: string | number| boolean };

// Resolve target for a given data-id
export type ResolveTarget = (
  id: string
) => { sectionIndex: number; subsectionIndex?: number } | null;

/**
 * Normalize section inputs from multiple possible shapes into SectionInputs.
 * - Accepts SectionInputs (returns as-is)
 * - Accepts arrays of pairs [{key,value}] (uses resolveTarget to place)
 * - Accepts maps of sectionKey -> pairs (uses resolveTarget to place)
 * - Accepts maps of key -> name->value (returns as-is)
 */
export function normalizeSectionInputs(
  inputs:
    | SectionInputs
    | KeyValuePair[]
    | { [sectionKey: string]: KeyValuePair[] }
    | { [key: string]: { [name: string]: string } },
  resolveTarget: ResolveTarget
): SectionInputs {
  // If already object-shaped section inputs (not arrays), return as-is unless it's a map of arrays
  if (inputs && typeof inputs === "object" && !Array.isArray(inputs)) {
    const maybe = inputs as { [k: string]: unknown };
    const values = Object.values(maybe);
    const looksLikePairsArray = values.length > 0 && Array.isArray(values[0]);
    if (!looksLikePairsArray) {
      return inputs as SectionInputs;
    }
  }

  const result: SectionInputs = {};

  const assignPair = (key: string, value: unknown) => {
    const target = resolveTarget(key);
    if (!target) return;
    const sectionKey = String(target.sectionIndex);
    const valStr = value != null ? String(value) : "";
    if (typeof target.subsectionIndex === "number") {
      const subKey = String(target.subsectionIndex);
      const sectionObj = (result[sectionKey] as { [subKey: string]: FormValues }) || {};
      const subObj = sectionObj[subKey] || {};
      subObj[key] = valStr;
      sectionObj[subKey] = subObj;
      result[sectionKey] = sectionObj;
    } else {
      const sectionVals = (result[sectionKey] as FormValues) || {};
      sectionVals[key] = valStr;
      result[sectionKey] = sectionVals;
    }
  };

  if (Array.isArray(inputs)) {
    (inputs as Array<{ key?: string; value?: unknown; Key?: string; Value?: unknown }>).forEach(
      (p) => {
        const k = (p.key ?? p.Key) as string | undefined;
        const v = p.value ?? p.Value ?? "";
        if (!k) return;
        assignPair(k, v);
      }
    );
    return result;
  }
  if (inputs && typeof inputs === "object") {
    const map = inputs as { [k: string]: KeyValuePair[] };
    Object.values(map).forEach((arr) => {
      if (!Array.isArray(arr)) return;
      arr.forEach((p) => assignPair(p.key, p.value));
    });
    return result;
  }
  return {};
}
